// SendKillSwitchPage — /hub/admin/send (Epic D2 · §A-3).
//
// The admin surface for the send-pipeline kill switch. v_send_health carries the
// real on/off state (enabled), the reason it was paused (paused_reason), and
// throughput/blocked counts. fn_send_pause(p_reason) pauses sending for EVERY
// client — p_reason is REQUIRED (no default; "why" is the first question the next
// person asks and is written to ops_send_config.paused_reason). fn_send_resume()
// takes no arguments.
//
// Both writes are optimistic with a revert on error and are never retried (they
// are writes) — a failed write surfaces the real error inline (knowledge-base §18:
// never fake a sent state; the page states the real `enabled` value from
// v_send_health). After a successful write the page refetches health so the
// server-authored fields (paused_at, counts) are authoritative, not the optimistic
// guess.
//
// Gated by the agent.manage capability (Brian and Aly — knowledge-base §14). The
// wall is the capability check here AND the database (every function resolves
// auth.uid() — 00_CONTRACT.md §6). A viewer without agent.manage sees <NotPermitted>
// and the page makes no API call.

import React, { useCallback, useEffect, useState } from "react";
import { HubShell } from "../../components/hub/shell";
import { NotPermitted } from "../../components/hub/kit";
import { ErrorState } from "../../components/shared/StateMessage";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { fmtDate } from "../../components/shared/format";
import { getSendHealth, pauseSending, resumeSending } from "../../api/conversations";

// v_send_health's columns fall back across plausible names (D6 re-verifies the
// live shape). A missing field degrades to null/0 rather than crashing.
const enabledOf = (h) => h?.enabled ?? h?.is_enabled ?? false;
const reasonOf = (h) => h?.paused_reason ?? h?.pause_reason ?? h?.reason ?? null;
const pausedAtOf = (h) => h?.paused_at ?? h?.paused_when ?? null;
const sentHourOf = (h) => h?.sent_last_hour ?? h?.sent_this_hour ?? 0;
const sentTodayOf = (h) => h?.sent_today ?? 0;
const blockedTodayOf = (h) => h?.blocked_today ?? 0;
const failedTodayOf = (h) => h?.failed_today ?? 0;
const waitingOf = (h) => h?.waiting_to_send ?? h?.waiting ?? 0;
const blockedTotalOf = (h) => h?.blocked_total ?? 0;

export default function SendKillSwitchPage() {
  const { can } = useWorkspace();
  const permitted = can("agent.manage");

  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [health, setHealth] = useState(null);
  const [writing, setWriting] = useState(false);
  const [writeError, setWriteError] = useState(null);
  const [pending, setPending] = useState(null); // null | 'pause' | 'resume'
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setStatus("loading");
    setWriteError(null);
    try {
      setHealth(await getSendHealth());
      setStatus("ready");
    } catch {
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    if (permitted) load();
  }, [permitted, load]);

  // Quietly refresh after a write (no loading flash) so server-authored fields
  // (paused_at, counts) become authoritative, not the optimistic guess.
  const refresh = useCallback(async () => {
    try {
      setHealth(await getSendHealth());
    } catch {
      /* keep optimistic; a future manual reload surfaces the error */
    }
  }, []);

  const onRequestPause = useCallback(() => {
    if (writing) return;
    setReason("");
    setWriteError(null);
    setPending("pause");
  }, [writing]);

  const onRequestResume = useCallback(() => {
    if (writing) return;
    setWriteError(null);
    setPending("resume");
  }, [writing]);

  const onConfirm = useCallback(async () => {
    if (writing || !pending) return;
    if (pending === "pause" && !reason.trim()) return; // reason required (guard)
    const prev = health;
    const optimistic =
      pending === "pause"
        ? { ...health, enabled: false, paused_reason: reason.trim() }
        : { ...health, enabled: true, paused_reason: null, paused_at: null };
    setPending(null);
    setWriting(true);
    setWriteError(null);
    setHealth(optimistic); // optimistic flip
    try {
      if (pending === "pause") await pauseSending(reason.trim());
      else await resumeSending();
      setReason("");
      await refresh();
    } catch (err) {
      setHealth(prev); // revert
      setWriteError(err?.message || "Couldn't update the send state.");
    } finally {
      setWriting(false);
    }
  }, [writing, pending, reason, health, refresh]);

  const onCancel = useCallback(() => {
    setPending(null);
    setReason("");
  }, []);

  if (!permitted) {
    return (
      <HubShell>
        <div className="p-6">
          <NotPermitted
            title="Not permitted"
            message="The send kill switch is an admin surface."
          />
        </div>
      </HubShell>
    );
  }

  const ready = status === "ready";
  const enabled = ready ? enabledOf(health) : false;

  return (
    <HubShell>
      <div className="mx-auto w-full max-w-3xl px-4 py-3 dark:border-gray-800 md:px-6 lg:px-10">
        <header className="mb-4">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Send kill switch
          </h1>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            Pausing stops outbound delivery for every client. Replies are saved
            as drafts and go out when sending resumes.
          </p>
        </header>

        {status === "loading" && (
          <div
            className="h-24 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-900"
            role="status"
            aria-label="Loading"
          />
        )}

        {status === "error" && (
          <ErrorState
            variant="card"
            title="Send health"
            message="Couldn't read send health. This is a backend view — retry, and report if it persists."
            onRetry={load}
          />
        )}

        {ready && (
          <div
            className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900"
            data-testid="send-health-card"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <span
                  className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    enabled
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                      : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                  }`}
                  data-testid="send-state"
                >
                  {enabled ? "Sending on" : "Sending paused"}
                </span>
                {!enabled && reasonOf(health) && (
                  <p
                    className="mt-2 text-sm text-gray-700 dark:text-gray-300"
                    data-testid="send-paused-reason"
                  >
                    {reasonOf(health)}
                  </p>
                )}
                {!enabled && pausedAtOf(health) && (
                  <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                    Paused {fmtDate(pausedAtOf(health))}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {enabled ? (
                  <button
                    type="button"
                    onClick={onRequestPause}
                    disabled={writing}
                    className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
                    data-testid="send-pause"
                  >
                    {writing ? "…" : "Pause sending"}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={onRequestResume}
                    disabled={writing}
                    className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-800 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300"
                    data-testid="send-resume"
                  >
                    {writing ? "…" : "Resume sending"}
                  </button>
                )}
              </div>
            </div>
            <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
              <Stat label="Sent last hour" value={sentHourOf(health)} />
              <Stat label="Sent today" value={sentTodayOf(health)} />
              <Stat label="Blocked today" value={blockedTodayOf(health)} />
              <Stat label="Failed today" value={failedTodayOf(health)} />
              <Stat label="Waiting to send" value={waitingOf(health)} />
              <Stat label="Blocked total" value={blockedTotalOf(health)} />
            </dl>
            {writeError && (
              <div className="mt-4">
                <ErrorState variant="strip" message={writeError} />
              </div>
            )}
          </div>
        )}

        {/* Pause dialog — required reason */}
        {pending === "pause" && (
          <Dialog title="Pause sending for every client?" testId="send-pause-dialog">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Outbound delivery stops immediately. Replies are saved as drafts and
              go out when sending resumes. A reason is required — it is shown to the
              next person who opens this page.
            </p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you pausing?"
              rows={3}
              className="mt-3 w-full rounded-md border border-gray-300 p-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              data-testid="send-pause-reason"
            />
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={onCancel}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                data-testid="send-pause-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={!reason.trim()}
                className="rounded-md border border-amber-400 bg-amber-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-40"
                data-testid="send-pause-confirm"
              >
                Pause sending
              </button>
            </div>
          </Dialog>
        )}

        {/* Resume dialog — confirm only */}
        {pending === "resume" && (
          <Dialog title="Resume sending for every client?" testId="send-resume-dialog">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              This resumes outbound delivery for every client. Drafted replies
              waiting in the queue will go out.
            </p>
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={onCancel}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                data-testid="send-resume-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirm}
                className="rounded-md border border-emerald-500 bg-emerald-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-600"
                data-testid="send-resume-confirm"
              >
                Resume sending
              </button>
            </div>
          </Dialog>
        )}
      </div>
    </HubShell>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <dt className="text-xs text-gray-400 dark:text-gray-500">{label}</dt>
      <dd className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
        {value ?? 0}
      </dd>
    </div>
  );
}

// Minimal centered modal. Inline so the page is standalone (no new kit import).
function Dialog({ title, children, testId }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      data-testid={testId}
    >
      <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-5 shadow-xl dark:border-gray-800 dark:bg-gray-900">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}
